import React, { Suspense, lazy, useEffect, useRef, useState } from "react";
import { useSearch } from "../SearchContext";
import { useNavigate, useParams } from "react-router-dom";
// import useTranslation from "./customHooks/translations";
import { _get } from "../services/apiClient";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
const MenuItem = lazy(() => import("./MenuItem"));

function MenuSection({
  handleClick,
  cart,
  setCart,
  products,
  totalCount,
  subCategories,
  subCatCounts = {},
  restaurantStatus,
  isServerSearch,
  isFetching,
  offset,
  totalPages,
}) {
  const {
    searchData,
    searchAllCategories,
    filtersubCategory,
    filter,
    setFilter,
    setFilterSubCategory,
  } = useSearch();
  const [categories, setCategories] = useState([]);
  const [categoryname, setCategoryName] = useState("");
  const elementRef = useRef(null);
  const translation = useTranslation();
  const { id } = useParams();
  // const [isVeg, setIsVeg] = useState(false);
  const [foodType, setFoodType] = useState("");
  // "" = all
  // "veg" = vegetarian
  // "nonveg" = non vegetarian
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();

  const getLocalizedText = (field) => {
    if (!field) return "";
    if (typeof field === "string") return field;
    if (typeof field === "object")
      return field[i18n.language] || field.en || "";
    return "";
  };

  // Helper to normalize and compare IDs (handles both string and object format)
  const normalizeId = (id) => {
    if (!id) return null;
    if (typeof id === "string") return id;
    if (typeof id === "object" && id.$oid) return id.$oid;
    if (typeof id === "object" && id._id) return id._id;
    if (typeof id === "object" && id.id) return id.id;
    return id;
  };

  const idsMatch = (id1, id2) => {
    return normalizeId(id1) === normalizeId(id2);
  };

  const isRTL = i18n.dir() === "rtl";
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [activeSubCat, setActiveSubCat] = useState("");

  const handleCategoryClick = (categoryId) => {
    navigate({
      pathname: `/products/${categoryId}`,
      search: location.search,
    });
  };

  const handleFoodFilter = (type) => {
    if (foodType === type) {
      setFoodType("");
      setFilter("");
    } else {
      setFoodType(type);
      setFilter(type);
    }
    // Automatically reset subcategory to 'All'
    setActiveSubCat("");
    if (typeof setFilterSubCategory === "function") setFilterSubCategory("");
  };

  const fetchCategoriesData = async () => {
    try {
      const response = await _get(`/categories`);
      setCategories(response.data);
      setCategoryName(response.data.find((x) => x.id == id)?.name || "");
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await fetchCategoriesData();
      setLoading(false);
    };

    if (id) loadData();
  }, [id]);

  // When category changes (back, or select another category), clear subcategory filter
  // so the previous category's filter doesn't hide all products in the new category.
  useEffect(() => {
    if (!id) return;
    setActiveSubCat("");
    if (typeof setFilterSubCategory === "function") setFilterSubCategory("");
  }, [id]);

  const searchAllMounted = useRef(false);
  useEffect(() => {
    if (!searchAllMounted.current) {
      searchAllMounted.current = true;
      return;
    }
    // When toggling 'search in all categories', clear the subcat filter so it doesn't 
    setActiveSubCat("");
    if (typeof setFilterSubCategory === "function") setFilterSubCategory("");
  }, [searchAllCategories]);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 400);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubCatFilter = (subCatId) => {
    if (subCatId === "") {
      setActiveSubCat("");
      if (typeof setFilterSubCategory === "function") setFilterSubCategory("");
      // Scroll to top immediately and force it
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      window.scrollTo(0, 0);
    } else {
      setActiveSubCat(subCatId);
      if (typeof setFilterSubCategory === "function")
        setFilterSubCategory(subCatId);
      setTimeout(() => {
        const element = document.getElementById(`subcat-${subCatId}`);
        if (element) {
          const offset = 200; // Account for sticky headers
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - offset;
          window.scrollTo({
            top: offsetPosition,
            behavior: "smooth",
          });
        }
      }, 100);
    }
    if (subCatId === "") {
      setActiveSubCat("");
      if (typeof setFilterSubCategory === "function") setFilterSubCategory("");
      if (typeof window !== "undefined" && window.sessionStorage) {
        window.sessionStorage.removeItem("filtersubCategory");
      }
      // Use location hash to force scroll to top after UI update
      setTimeout(() => {
        window.location.hash = "top";
        setTimeout(() => {
          window.scrollTo({ top: 0, behavior: "smooth" });
        }, 50);
      }, 150);
      return;
    }
    // Subcategory scroll
    setActiveSubCat(subCatId);
    if (typeof setFilterSubCategory === "function")
      setFilterSubCategory(subCatId);
    setTimeout(() => {
      const element = document.getElementById(`subcat-${subCatId}`);
      if (element) {
        const offset = 200; // Account for sticky headers
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;
        window.scrollTo({
          top: offsetPosition,
          behavior: "smooth",
        });
      }
    }, 100);
  };

  // When landing with ?subcat= (e.g. from banner click), apply same behavior as clicking a subcat chip:
  // set context filter + active subcat so the subcat section loads and highlights like normal flow.
  useEffect(() => {
    try {
      const params = new URLSearchParams(location.search);
      const subcatParam = params.get("subcat");
      if (subcatParam && id) {
        const normalizedSubcat = normalizeId(subcatParam) || subcatParam;
        handleSubCatFilter(normalizedSubcat);

        params.delete("subcat");
        params.delete("product");
        const newSearch = params.toString();
        navigate({ pathname: location.pathname, search: newSearch ? `?${newSearch}` : "" }, { replace: true });
      }
    } catch (e) {
      // ignore
    }
  }, [location.search, id]);

  const filteredProductsClient = products.filter((item) => {
    const localizedProductName = getLocalizedText(item.productName);
    const matchesSearch = searchData
      ? localizedProductName.toLowerCase().includes(searchData.toLowerCase())
      : true;
    const matchesFoodFilter =
      foodType === "veg"
        ? item.foodType === "veg"
        : foodType === "nonveg"
          ? item.foodType === "nonveg"
          : true;
    return matchesSearch && matchesFoodFilter;
  });

  // Apply food filter to any list
  const applyFoodFilter = (list) => {
    if (!foodType) return list;
    return list.filter((p) =>
      foodType === "veg" ? p.foodType === "veg" : p.foodType === "nonveg"
    );
  };

  const hasServerSubCatCounts = subCatCounts && typeof subCatCounts === "object";

  const allChipCount = hasServerSubCatCounts ? Object.values(subCatCounts).reduce((a, b) => a + b, 0) : totalCount;

  const shouldApplySubcatGuard = !!activeSubCat;

  const uiActiveSubCat = shouldApplySubcatGuard ? activeSubCat : "";

  const categorySubCategories = React.useMemo(() => {
    // When searching across all categories, show chips from all categories.
    if (searchAllCategories) return subCategories;
    return subCategories.filter((subCat) => idsMatch(subCat.category, id));
  }, [subCategories, id, searchAllCategories]);

  const normalizedSubCategories = React.useMemo(() => {
    // In all-categories search mode, don't inject any virtual "Others" section.
    if (searchAllCategories) return categorySubCategories;
    if (!products || products.length === 0) return categorySubCategories;

    const hasNoSubCategory = products.some(
      (p) => idsMatch(p.subcategory, p.category)
    );

    if (!hasNoSubCategory) return categorySubCategories;

    const exists = categorySubCategories.some(
      (s) => idsMatch(s._id, id)
    );

    if (exists) return categorySubCategories;

    return [
      {
        _id: id, // categoryId
        subCat: { en: getLocalizedText(categoryname) || "Others" },
        isVirtual: true,
      },
      ...categorySubCategories,
    ];
  }, [products, categorySubCategories, id, categoryname, searchAllCategories]);

  const [prevFilters, setPrevFilters] = useState({
    searchData,
    filtersubCategory,
    filter,
    id,
    searchAllCategories
  });

  const isTransitioning = 
    searchData !== prevFilters.searchData ||
    filtersubCategory !== prevFilters.filtersubCategory ||
    filter !== prevFilters.filter ||
    id !== prevFilters.id ||
    searchAllCategories !== prevFilters.searchAllCategories;

  useEffect(() => {
    if (isTransitioning) {
      setPrevFilters({ searchData, filtersubCategory, filter, id, searchAllCategories });
    }
  }, [searchData, filtersubCategory, filter, id, searchAllCategories, isTransitioning]);

  return (
    <div className="container position-relative">
      {loading ? (
        <div className="flex items-center justify-center h-[80vh]">
          <div className="relative">
            <div className="w-14 h-14 border-4 border-orange-200 rounded-full"></div>
            <div className="absolute top-0 left-0 w-14 h-14 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        </div>

      ) : (
        <>
          <div className="sticky top-[100px] sm:top-[112px] z-30 px-3 sm:px-4 py-2.5 sm:py-3 bg-white border-b-2 border-orange-200/60 shadow-lg">
            <div className="flex items-center justify-between gap-2">
              {/* Breadcrumb */}
              <div className="flex items-center space-x-1 sm:space-x-1.5 min-w-0 flex-1">
                <button
                  type="button"
                  onClick={() =>
                    navigate({
                      pathname: "/",
                      search: location.search,
                    })
                  }
                  className="text-gray-400 hover:text-orange-600 transition-all duration-200 hover:scale-110 flex-shrink-0"
                >
                  <svg
                    className="w-4 h-4 sm:w-4.5 sm:h-4.5"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
                  </svg>
                </button>
                <svg
                  className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-gray-300 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
                <span className="text-xs sm:text-sm font-medium text-gray-800 truncate">
                  <span className="font-semibold">
                    {getLocalizedText(categoryname)}
                  </span>
                </span>
              </div>

              {/* Veg Filter Toggle */}
              <div className="flex gap-2">
                <button
                  onClick={() => handleFoodFilter("veg")}
                  className={`px-3 py-1 rounded-full text-xs font-medium border transition ${foodType === "veg"
                    ? "bg-green-100 border-green-500 text-green-700"
                    : "bg-white border-gray-300 text-gray-600"
                    }`}
                >
                  🥬 Veg
                </button>

                <button
                  onClick={() => handleFoodFilter("nonveg")}
                  className={`px-3 py-1 rounded-full text-xs font-medium border transition ${foodType === "nonveg"
                    ? "bg-red-100 border-red-500 text-red-700"
                    : "bg-white border-gray-300 text-gray-600"
                    }`}
                >
                  🍗 Non-Veg
                </button>
              </div>
            </div>

            {/* Subcategory Filter Chips */}
            <div className="mt-3 pt-3 pb-3 border-t border-orange-200/50">
              {normalizedSubCategories.length > 0 && (
                <div className="overflow-x-auto scrollbar-hide">
                  <div className="flex gap-2 px-1">
                    {/* All button */}
                    <button
                      onClick={() => handleSubCatFilter("")}
                      className={`flex-shrink-0 px-4 py-2 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap ${uiActiveSubCat === ""
                        ? "bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-md scale-105"
                        : "bg-white text-gray-700 border border-gray-300 hover:border-orange-400 hover:text-orange-600"
                        }`}
                    >
                      All {allChipCount > 0 ? `(${allChipCount})` : ""}
                    </button>
                    {normalizedSubCategories.map((subCat) => {
                      // Use server-provided count (stable) when no food filter; fall back to client count when food-filtered
                      const serverCount = subCatCounts[normalizeId(subCat._id)];
                      const matchingProducts = products.filter((p) =>
                        idsMatch(subCat._id, id)
                          ? idsMatch(p.subcategory, p.category)
                          : idsMatch(p.subcategory, subCat._id)
                      );
                      const clientCount = applyFoodFilter(matchingProducts).length;
                      const displayCount = hasServerSubCatCounts ? (serverCount ?? 0) : clientCount;

                      if (displayCount <= 0) {
                        return null;
                      }

                      return (
                        <button
                          key={subCat._id}
                          onClick={() => handleSubCatFilter(subCat._id)}
                          className={`flex-shrink-0 px-4 py-2 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap ${idsMatch(uiActiveSubCat, subCat._id)
                            ? "bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-md scale-105"
                            : "bg-white text-gray-700 border border-gray-300 hover:border-orange-400 hover:text-orange-600"
                            }`}
                        >
                          {getLocalizedText(subCat.subCat)} {displayCount > 0 ? `(${displayCount})` : ""}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>

          {restaurantStatus === "offline" && ( //  Added message in the menu top
            <div
              className="alert alert-danger text-center fw-bold py-2 mt-2"
              role="alert"
              style={{ fontSize: "1.25rem", color: "#dc3545" }}
            >
              {t("restaurantOffline")}
            </div>
          )}

          {isServerSearch && (
            <>
              {products.length > 0 ? (
                (() => {
                  return normalizedSubCategories.map((section, index) => {
                    if (shouldApplySubcatGuard && !idsMatch(section._id, activeSubCat)) {
                      return null;
                    }

                    const filteredSectionItems = filteredProductsClient.filter((item) =>
                      idsMatch(section._id, id)
                        ? idsMatch(item.subcategory, item.category)
                        : idsMatch(item.subcategory, section._id)
                    );

                    if (filteredSectionItems.length === 0) return null;

                    const itemsToRender = filteredSectionItems;

                    return (
                      <div key={index} id={`subcat-${section._id}`}>
                        <div
                          className="py-3 mb-3 md:py-4 md:mb-4 px-3 md:px-4 sticky top-[230px] sm:top-[240px] z-20 bg-white"
                          name="fixedMenuHeader"
                        >
                          <div className="flex items-center gap-3">
                            <div className="h-8 w-1 bg-gradient-to-b from-orange-500 to-red-500 rounded-full"></div>
                            <div>
                              <h6 className="text-sm md:text-base font-medium text-gray-900 mb-0">
                                {getLocalizedText(section.subCat)}
                              </h6>
                            </div>
                          </div>
                        </div>
                        <div className="most_sale">
                          <div className="row mb-3 mt-0">
                            <Suspense
                              fallback={
                                <div>
                                  <p>
                                    <i>Loading items...</i>
                                  </p>
                                </div>
                              }
                            >
                              {itemsToRender.map(
                                (detail, detailIndex) => (
                                  <MenuItem
                                    key={`${detail._id || detailIndex}-${i18n.language
                                      }`}
                                    MenuItem={detail}
                                    handleClick={handleClick}
                                    products={products}
                                    cart={cart}
                                    setCart={setCart}
                                  />
                                ),
                              )}
                            </Suspense>
                          </div>
                        </div>
                      </div>
                    );
                  });
                })()
              ) : (
                !(isFetching || isTransitioning) && (
                  <h5 className="text-danger text-center mt-4">
                    Sorry, no items matched your search.
                  </h5>
                )
              )}
            </>
          )}

          {!isServerSearch && (
            <>
              {(() => {
                return normalizedSubCategories.map((section, index) => {
                  if (shouldApplySubcatGuard && !idsMatch(section._id, activeSubCat)) {
                    return null;
                  }

                  const filteredSectionItems = filteredProductsClient.filter(
                    (item) => {
                      if (idsMatch(section._id, id)) {
                        // ✅ No Subcategory - items with no real subcategory
                        return idsMatch(item.subcategory, item.category);
                      }
                      // Normal subcategory
                      return idsMatch(item.subcategory, section._id);
                    }
                  );

                  if (filteredSectionItems.length === 0) return null;

                  const itemsToRender = filteredSectionItems;

                  return (
                    <div key={index} id={`subcat-${section._id}`}>
                      <div
                        className="py-3 mb-3 md:py-4 md:mb-4 px-3 md:px-4 sticky top-[230px] sm:top-[240px] z-20 bg-white"
                        name="fixedMenuHeader"
                      >
                        <div className="flex items-center gap-3">
                          <div className="h-8 w-1 bg-gradient-to-b from-orange-500 to-red-500 rounded-full"></div>
                          <div>
                            <h6 className="text-sm md:text-base font-medium text-gray-900 mb-0">
                              {getLocalizedText(section.subCat)}
                            </h6>
                          </div>
                        </div>
                      </div>
                      <div className="most_sale">
                        <div className="row mb-3 mt-0">
                          <Suspense
                            fallback={
                              <div>
                                <p>
                                  <i>Loading items...</i>
                                </p>
                              </div>
                            }
                          >
                            {itemsToRender.map((detail, detailIndex) => (
                              <MenuItem
                                key={`${detail._id || detailIndex}-${i18n.language
                                  }`}
                                MenuItem={detail}
                                handleClick={handleClick}
                                products={products}
                                cart={cart}
                                setCart={setCart}
                              />
                            ))}
                          </Suspense>
                        </div>
                      </div>
                    </div>
                  );
                });
              })()}

              {(searchData || filtersubCategory || foodType) &&
                filteredProductsClient.length === 0 &&
                !(isFetching || isTransitioning) && (
                  <h5 className="text-danger text-center mt-4">
                    {t("filteredItemDesc")}
                  </h5>
                )}

              {/* {products.length === 0 && (
                <h5 className="text-danger text-center mt-4">
                  {translation.filterditems}
                </h5>
              )}*/}
            </>
          )}

          {(isFetching || isTransitioning) && (
            <div className="flex items-center justify-center py-4 mb-20">
              <div className="relative">
                <div className="w-14 h-14 border-4 border-orange-200 rounded-full"></div>
                <div className="absolute top-0 left-0 w-14 h-14 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
              </div>
            </div>
          )}

          {!(isFetching || isTransitioning) && offset >= totalPages && products.length > 0 && (
            <div className="text-center py-4 mb-20 text-gray-500 text-sm">
              <p>✨ You've reached the end</p>
            </div>
          )}

          <div
            className="dropdown dropup"
            style={{
              position: "fixed",
              bottom: "80px",
              ...(isRTL ? { left: "20px" } : { right: "20px" }),
              zIndex: "60",
            }}
          >
            <button
              id="menucategories"
              className="btn btn-sm d-flex align-items-center gap-2 shadow-xl hover:shadow-2xl transition-all duration-300"
              type="button"
              data-bs-toggle="dropdown"
              aria-expanded="false"
              style={{
                backgroundColor: "white",
                border: "2px solid #fed7aa",
                borderRadius: "16px",
                padding: "12px 18px",
                color: "#ea580c",
                fontWeight: "600",
                fontSize: "14px",
              }}
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              <span>{t("categories")}</span>
            </button>
            <ul
              className={`dropdown-menu border-0 shadow-2xl ${isRTL ? "dropdown-menu-start" : "dropdown-menu-end"
                }`}
              style={{
                borderRadius: "16px",
                padding: "12px",
                minWidth: "260px",
                maxWidth: "300px",
                maxHeight: "420px",
                overflowY: "auto",
                position: "absolute",
                ...(isRTL
                  ? { left: "0", right: "auto" }
                  : { right: "0", left: "auto" }),
                bottom: "100%",
                marginBottom: "12px",
                transform: "none",
                border: "2px solid #fed7aa",
              }}
            >
              <li
                style={{
                  position: "sticky",
                  top: "-8px",
                  backgroundColor: "white",
                  zIndex: 10,
                  margin: "0 -8px",
                  padding: "12px 16px 8px",
                  borderBottom: "1px solid #f3f4f6",
                }}
              >
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  {t("categories")}
                </div>
              </li>
              {categories.map((section, index) => (
                <li key={index} style={{ padding: "0 4px" }}>
                  <button
                    className="dropdown-item rounded-lg text-sm font-medium transition-colors w-100 text-start"
                    onClick={() => handleCategoryClick(section.id)}
                    style={{
                      backgroundColor:
                        section.id == id ? "#FFF7ED" : "transparent",
                      color: section.id == id ? "#EA580C" : "#374151",
                      padding: "12px 14px",
                      marginBottom: "2px",
                      border: "none",
                      display: "block",
                      width: "100%",
                    }}
                  >
                    {getLocalizedText(section.name)}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </>
      )}

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className={`fixed bottom-20 sm:bottom-6 z-[70] w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-orange-500 via-orange-600 to-red-600 text-white rounded-full shadow-2xl hover:shadow-orange-500/50 transform hover:scale-110 hover:-translate-y-1 transition-all duration-300 flex items-center justify-center group ring-2 ring-orange-200 ring-offset-2 hover:animate-pulse-glow ${isRTL ? "right-4 sm:right-6" : "left-4 sm:left-6"
            }`}
          aria-label="Scroll to top"
        >
          <svg
            className="w-5 h-5 sm:w-6 sm:h-6 transform group-hover:-translate-y-0.5 group-hover:scale-110 transition-transform duration-200"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2.5}
              d="M5 10l7-7m0 0l7 7m-7-7v18"
            />
          </svg>
        </button>
      )}
    </div>
  );
}

export default MenuSection;